import { IconCaretRightFilled, IconCaretUpFilled } from "@tabler/icons-react"
import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { formatNumber, formatPercent } from "lopermut"
import { useSegmentGroupQuery } from "modules/segments/hooks/useSegmentGroupQuery"
import { useSegmentGroupStatsQuery } from "modules/segments/hooks/useSegmentGroupStatsQuery"
import { useTranslation } from "react-i18next"

type Props = {
  segmentGroupId: string
}

export function SegmentGroupMetrics({ segmentGroupId }: Props) {
  const { t } = useTranslation()

  const { data: segmentGroup, isPending: isQueryPending } = useSegmentGroupQuery({
    id: segmentGroupId,
  })
  const { data: segmentGroupStats, isPending: isStatsPending } = useSegmentGroupStatsQuery({
    id: segmentGroupId,
  })

  const isPending = isQueryPending || isStatsPending

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <MetricCard
        currentValue={segmentGroup ? formatNumber({ amount: segmentGroup.segments.length }) : ""}
        isPending={isPending}
        title={t("segments.metrics.segments.title")}
        metric={
          <div className="flex flex-row items-center gap-1">
            {segmentGroupStats?.newSegments && segmentGroupStats.newSegments > 0 ? (
              <IconCaretUpFilled className="size-5" />
            ) : (
              <IconCaretRightFilled className="size-5" />
            )}
            <span>
              {t("segments.metrics.segments.newSegments", {
                count: segmentGroupStats?.newSegments ?? 0,
              })}
            </span>
          </div>
        }
        metricTooltip={t("segments.metrics.segments.newSegmentsTooltip")}
      />
      <MetricCard
        currentValue={formatPercent({
          amount:
            ((segmentGroupStats?.customersInGroup ?? 0) / (segmentGroup?.customersCount ?? 0)) *
            100,
          fix: 0,
        })}
        metric={
          segmentGroupStats
            ? t("segments.metrics.coverage.customersCount", {
                count: segmentGroupStats.customersInGroup,
                formattedCount: formatNumber({ amount: segmentGroupStats.customersInGroup }),
              })
            : ""
        }
        isPending={isPending}
        title={t("segments.metrics.coverage.title")}
        tooltip={t("segments.metrics.coverage.deltaTooltip")}
        metricTooltip={t("segments.metrics.coverage.tooltip")}
      />
      <MetricCard
        currentValue={formatPercent({ amount: segmentGroupStats?.currentStabilityScore })}
        tooltip={t("segments.metrics.stabilityScore.tooltip")}
        deltaPercentage={
          segmentGroupStats?.currentStabilityScore && segmentGroupStats?.previousStabilityScore
            ? (segmentGroupStats.currentStabilityScore - segmentGroupStats.previousStabilityScore) /
              segmentGroupStats.previousStabilityScore
            : 0
        }
        deltaTooltip={t("segments.metrics.stabilityScore.deltaTooltip")}
        isPending={isPending}
        title={t("segments.metrics.stabilityScore.title")}
      />
    </div>
  )
}
